import type {
  SecureAccessChange,
  SecureProjectStatus,
} from "features/secureUploads/secureUploads";

export const PROJECT_NAME_MAX = 50;
export const PROJECT_DESCRIPTION_MAX = 140;

export interface ProjectDetails {
  clientId: number | null;
  name: string;
  description: string;
}

export type ProjectDetailsErrors = Partial<
  Record<"client" | "name" | "description", string>
>;

/** What keeps the Details step from going on, field by field. */
export function detailsErrors(details: ProjectDetails): ProjectDetailsErrors {
  const errors: ProjectDetailsErrors = {};
  const name = details.name.trim();
  if (!details.clientId) errors.client = "Choose a client.";
  if (!name) errors.name = "Name the project.";
  else if (name.length > PROJECT_NAME_MAX)
    errors.name = `At most ${PROJECT_NAME_MAX} characters.`;
  if (details.description.trim().length > PROJECT_DESCRIPTION_MAX)
    errors.description = `At most ${PROJECT_DESCRIPTION_MAX} characters.`;
  return errors;
}

/** Who a new member list adds and removes. */
export function memberChanges(
  before: number[],
  after: number[],
): { added: number[]; removed: number[] } {
  const was = new Set(before);
  const will = new Set(after);
  return {
    added: after.filter((id) => !was.has(id)),
    removed: before.filter((id) => !will.has(id)),
  };
}

export function sameMembers(a: number[], b: number[]): boolean {
  const { added, removed } = memberChanges(a, b);
  return added.length === 0 && removed.length === 0;
}

export function countOf(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "ADDING - 1 USER". */
export function changeHeading(kind: "adding" | "removing", n: number): string {
  return `${kind.toUpperCase()} - ${countOf(n, "USER", "USERS")}`;
}

/** The line under a person in Confirm Access Changes. */
export function accessLine(
  kind: "adding" | "removing",
  change: SecureAccessChange,
): string {
  const files = countOf(change.files, "file", "files");
  if (kind === "adding") return `Gains access to ${files}.`;
  if (change.shared)
    return `Loses access to ${files}, ${change.shared} of them shared with them.`;
  return `Loses access to ${files}.`;
}

/** The Manage Project dropdown: on a closed project only access, reopening and
 * deleting are left. */
export function manageProjectItems(project: { status: SecureProjectStatus }) {
  const closed = project.status === "closed";
  return [
    { kind: "access" as const, label: "Manage Access" },
    ...(closed
      ? [{ kind: "reopen" as const, label: "Reopen Project" }]
      : [{ kind: "edit" as const, label: "Edit Project" }]),
    ...(closed ? [] : [{ kind: "close" as const, label: "Close Project" }]),
    { kind: "delete" as const, label: "Delete Project", danger: true },
  ];
}

/** What the member picker says when it lists nobody. */
export function memberPickerEmptyText(
  candidates: number,
  offered: number,
): string {
  if (candidates === 0)
    return "No client users have access to Secure Exchange yet";
  if (offered === 0)
    return "All users with access to Secure Exchange are on the project";
  return "No client users match";
}

/** "" is All Statuses. */
export type ProjectStatusFilter = SecureProjectStatus | "";

/** Where a person's choice of the Projects filter is kept, in this browser. */
export function projectStatusKey(userId: number | null | undefined) {
  return userId ? `secureExchangeProjectStatus:${userId}` : null;
}

/** The kept choice, or Open: the Projects tab opens on the open projects. */
export function readProjectStatus(stored: unknown): ProjectStatusFilter {
  return stored === "" || stored === "open" || stored === "closed"
    ? stored
    : "open";
}

/** What an empty Projects tab says. */
export function projectsEmptyText({
  search,
  status,
  canOrganize,
}: {
  search: string;
  status: ProjectStatusFilter;
  canOrganize: boolean;
}): string {
  if (search) return "No projects match.";
  if (status === "open")
    return canOrganize
      ? "No open projects. Start one with New Project."
      : "You are not on any open project.";
  if (status === "closed") return "No closed projects.";
  return canOrganize
    ? "No projects yet. Start one with New Project."
    : "You are not on any project yet.";
}
